// API bindings: rulebook (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { ActiveRulebookList, ApplicableRuleList, RulebookPolicyCheckResult, RulebookPolicyCheckBody, RulebookEvaluationList, RulebookEvaluationExport } from "./definitions";

export class ApiRulebook {
    /**
     * Method: GET
     * Path: /rulebook
     * Lists the rulebooks in force in this connector
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListActiveRulebooks(queryParams: ListActiveRulebooksQueryParameters): RequestParams<ActiveRulebookList, CommonAuthenticatedErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/rulebook` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /rulebook/rules
     * Lists the approved rules that apply, gathered from every rulebook in force.
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListApplicableRules(queryParams: ListApplicableRulesQueryParameters): RequestParams<ApplicableRuleList, CommonAuthenticatedErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/rulebook/rules` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /rulebook/check/usage-policy
     * Checks a usage policy against the rules of the rulebooks in force
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CheckUsagePolicyAgainstRulebook(body: RulebookPolicyCheckBody): RequestParams<RulebookPolicyCheckResult, CheckUsagePolicyAgainstRulebookErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/rulebook/check/usage-policy`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /rulebook/evaluations
     * Lists the evaluations against the rulebook kept as evidence.
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListRulebookEvaluations(queryParams: ListRulebookEvaluationsQueryParameters): RequestParams<RulebookEvaluationList, ListRulebookEvaluationsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/rulebook/evaluations` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "TRANSFER_NOT_FOUND", handler.notFoundTransferNotFound)
                    .add(404, "AGREEMENT_NOT_FOUND", handler.notFoundAgreementNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /rulebook/evaluations/export
     * Exports the evaluations against the rulebook kept as evidence, as a JSON document that
     * can be verified without the connector.
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ExportRulebookEvaluations(queryParams: ExportRulebookEvaluationsQueryParameters): RequestParams<RulebookEvaluationExport, ExportRulebookEvaluationsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/rulebook/evaluations/export` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "TRANSFER_NOT_FOUND", handler.notFoundTransferNotFound)
                    .add(404, "AGREEMENT_NOT_FOUND", handler.notFoundAgreementNotFound)
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Query parameters for ListActiveRulebooks
 */
export interface ListActiveRulebooksQueryParameters {
    /**
     * Page number (1-based). Default: 1
     */
    page?: number;

    /**
     * Items per page. Default: 10, Max: 256
     */
    pageSize?: number;
}

/**
 * Query parameters for ListApplicableRules
 */
export interface ListApplicableRulesQueryParameters {
    /**
     * Narrows the list to a scope
     */
    scope?: string;

    /**
     * Themes of the dataset, separated by commas. A rule limited to other themes is left out. Leave both empty to get every rule
     */
    themes?: string;

    /**
     * Keywords of the dataset, separated by commas. Leave both empty to get every rule
     */
    keywords?: string;

    /**
     * Page number (1-based). Default: 1
     */
    page?: number;

    /**
     * Items per page. Default: 100, Max: 256. Larger than elsewhere because the caller usually wants every rule that applies, to give them to the assistant
     */
    pageSize?: number;
}

/**
 * Error handler for CheckUsagePolicyAgainstRulebook
 */
export type CheckUsagePolicyAgainstRulebookErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ListRulebookEvaluations
 */
export type ListRulebookEvaluationsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * The agreement does not exist
     */
    notFoundAgreementNotFound: () => void;

    /**
     * The transfer does not exist
     */
    notFoundTransferNotFound: () => void;
};

/**
 * Query parameters for ListRulebookEvaluations
 */
export interface ListRulebookEvaluationsQueryParameters {
    /**
     * Narrows to a negotiation. Requires negotiation.read
     */
    negotiation?: string;

    /**
     * Narrows to the negotiation of an agreement. Requires negotiation.read
     */
    agreement?: string;

    /**
     * Narrows to the negotiation of the agreement of a transfer. Requires transfer.read
     */
    transfer?: string;

    /**
     * Narrows to a dataset. Requires dataset.read
     */
    dataset?: string;

    /**
     * Narrows to what was evaluated - eg: USAGE_POLICY,OFFER,OFFER_SENT
     */
    artifactType?: string;

    /**
     * Narrows to an outcome - eg: PASSED,WARNED,BLOCKED,REVIEW,REJECTED
     */
    outcome?: string;

    /**
     * Page number (1-based). Default: 1
     */
    page?: number;

    /**
     * Items per page. Default: 10, Max: 256
     */
    pageSize?: number;
}

/**
 * Error handler for ExportRulebookEvaluations
 */
export type ExportRulebookEvaluationsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * The agreement does not exist
     */
    notFoundAgreementNotFound: () => void;

    /**
     * The transfer does not exist
     */
    notFoundTransferNotFound: () => void;
};

/**
 * Query parameters for ExportRulebookEvaluations
 */
export interface ExportRulebookEvaluationsQueryParameters {
    /**
     * Narrows to a negotiation. Requires negotiation.read
     */
    negotiation?: string;

    /**
     * Narrows to the negotiation of an agreement. Requires negotiation.read
     */
    agreement?: string;

    /**
     * Narrows to the negotiation of the agreement of a transfer. Requires transfer.read
     */
    transfer?: string;

    /**
     * Narrows to a dataset. Requires dataset.read
     */
    dataset?: string;

    /**
     * Narrows to what was evaluated - eg: USAGE_POLICY,OFFER,OFFER_SENT
     */
    artifactType?: string;

    /**
     * Narrows to an outcome - eg: PASSED,WARNED,BLOCKED,REVIEW,REJECTED
     */
    outcome?: string;
}

